import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, BookOpen, Clock, ArrowRight } from "lucide-react";
import Pagination from "@/components/Pagination";
import usePaginationLinks from "@/hooks/usePaginationLinks";

const ALL = "__all__";
const PAGE_SIZE = 12;

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState({});
  const [loading, setLoading] = useState(true);
  const cat = params.get("category") || "";
  const q = params.get("search") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchData = async () => {
    setLoading(true);
    const sp = new URLSearchParams();
    if (cat) sp.append("category", cat);
    if (q) sp.append("search", q);
    sp.append("limit", String(PAGE_SIZE));
    sp.append("skip", String((page - 1) * PAGE_SIZE));
    const { data } = await api.get(`/articles?${sp}`);
    setItems(data.items); setTotal(data.total); setCats(data.categories);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const oldTitle = document.title;
    const oldDesc = document.querySelector('meta[name="description"]')?.content;
    const oldCanon = document.querySelector('link[rel="canonical"]')?.href;

    document.title = "Blog | hireds.in — Career Guides, Interview Prep, Finance";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.content = "Long-form, original guides on Indian govt-job preparation, interviews, resume, finance & recruitment processes by Dileshwar Vitthale and the hireds.in editorial team.";
    const canon = document.querySelector('link[rel="canonical"]');
    if (canon) canon.href = "https://hireds.in/blog";

    return () => {
      document.title = oldTitle;
      if (desc && oldDesc) desc.content = oldDesc;
      if (canon && oldCanon) canon.href = oldCanon;
    };
  // eslint-disable-next-line
  }, [params]);

  usePaginationLinks("/blog", Object.fromEntries(params), page, totalPages);

  const setCat = (c) => {
    const np = new URLSearchParams(params);
    if (c === ALL || !c) np.delete("category"); else np.set("category", c);
    np.delete("page");
    setParams(np);
  };

  const goToPage = (p) => {
    const np = new URLSearchParams(params);
    if (p > 1) np.set("page", String(p)); else np.delete("page");
    setParams(np);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10" data-testid="blog-page">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-wider font-bold text-orange-600">Editorial</div>
        <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mt-1" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>The hireds.in Blog</h1>
        <p className="text-slate-600 mt-2 max-w-2xl text-base leading-relaxed">Long-form, original articles on careers, interviews, finance, recruitment processes and education for Indian professionals and govt-job aspirants.</p>
      </header>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <form className="relative flex-1 max-w-md" onSubmit={(e)=>{e.preventDefault(); const f=new FormData(e.target); const v=f.get('q'); const np=new URLSearchParams(params); if(v) np.set('search', v); else np.delete('search'); setParams(np);}}>
          <label htmlFor="blog-search" className="sr-only">Search articles</label>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true"/>
          <Input id="blog-search" name="q" defaultValue={q} placeholder="Search articles..." aria-label="Search articles" className="pl-9" data-testid="blog-search"/>
        </form>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" role="tablist" aria-label="Article categories">
        <button data-testid="cat-all" onClick={()=>setCat(ALL)} aria-pressed={!cat} className={`px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${!cat?'bg-slate-900 text-white':'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>All ({total})</button>
        {Object.entries(cats).map(([slug, label]) => (
          <button key={slug} data-testid={`cat-${slug}`} onClick={()=>setCat(slug)} aria-pressed={cat===slug} className={`px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${cat===slug?'bg-orange-600 text-white':'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>{label}</button>
        ))}
      </div>

      {loading && <div className="text-slate-500" data-testid="articles-loading">Loading…</div>}

      {!loading && items.length === 0 && <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500" data-testid="no-articles">No articles found.</div>}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((a, i) => (
          <Link key={a.id} to={`/blog/${a.slug}`} data-testid={`article-card-${i}`} className="group bg-white border border-slate-200 rounded-lg p-5 hover:border-orange-300 hover:shadow-md transition-all">
            <div className="flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 font-bold uppercase tracking-wider">
                <BookOpen className="w-3 h-3"/>{cats[a.category] || a.category}
              </span>
              <span className="text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{a.reading_time} min read</span>
            </div>
            <h2 className="font-extrabold text-lg text-slate-900 mt-3 leading-snug group-hover:text-orange-600 transition-colors" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>{a.title}</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-3">{a.excerpt}</p>
            <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
              <time dateTime={(a.published_date||"").slice(0,10)}>{fmtDate(a.published_date)}</time>
              <span className="font-semibold text-slate-700 inline-flex items-center gap-1 group-hover:text-orange-600">Read <ArrowRight className="w-3 h-3"/></span>
            </div>
          </Link>
        ))}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={goToPage} />
    </div>
  );
}
